import React, { useContext } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useMediaQuery } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { ExportToExcel } from 'library';
import { useUserRolePermissions } from 'hooks';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ButtonGroup from '@mui/material/ButtonGroup';
import * as R from 'ramda';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import { hasAllRoles, userRoles } from 'utils/currentUserRoles';
import { DRAWER_TYPES } from 'utils/constants';
import { DrawerContext } from 'common/drawer/DrawerContext';
import AutoOneToOneDrawer from '../AutoReconsignments/AutoOneToOneDrawer';
import OneToOneReconsignment from '../reconsignmentsDrawer/OneToOneDrawer';

const Header = props => {
  const { definedValues, fetchCSVReport, tab, urlPrefix } = props;
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { currentAffiliate, isProductOwnerManage, isInventoryOwner } = useUserRolePermissions();
  const navigate = useNavigate();

  const { setDrawerComponentProps } = useContext(DrawerContext);

  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    ...definedValues,
  };

  const { startDate, endDate } = definedValues;

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClick = () => {
    fetchCSVReport(urlPrefix, params)
      .then(({ data }) => {
        return new Blob([data], { type: 'text/csv' });
      })
      .then(blob => {
        const removeDash = R.compose(R.join('_'), R.split('-'))(urlPrefix);
        R.equals('manual', tab)
          ? downloadBlob(blob, `${removeDash}_report_${startDate}_to_${endDate}.csv`)
          : downloadBlob(blob, `${removeDash}_report.csv`);
      })
      .catch(() => {
        // do nothing
      });
  };

  const ContainedMDropdownMenuButton = _props => (
    <MDropdownMenuButton variant="contained" {..._props} />
  );

  const rolesAllowed = hasAllRoles([userRoles.PRODUCT_OWNERSHIP_MANAGE, userRoles.INVENTORY_OWNER]);

  const DrawerValue = 'create';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
      <Box sx={{ display: 'flex' }}>
        <Typography sx={{ mr: 1 }} variant="h5">
          {lgUp ? 'Reconsignments Overview' : 'Overview'}
        </Typography>
        <Tooltip title="Please note that all Reconsignments adhere to our First In First Out (FIFO) policy.">
          <InfoRoundedIcon sx={{ color: 'graphic.main', cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <Box
        sx={{
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          display: { xs: 'none', lg: 'inline-flex' },
        }}
      />
      <Box
        sx={{
          display: { lg: 'inline-flex', xs: 'none' },
        }}
      >
        <IconButton
          aria-label="Export to Excel"
          onClick={handleClick}
          size="large"
          sx={{
            background: 'white',
            borderRadius: 24,
            boxShadow: 1,
            marginRight: isProductOwnerManage ? 2 : 0,
            padding: '6px',
          }}
          variant="text"
        >
          <ExportToExcel sx={{ fill: 'primary.main' }} />
        </IconButton>
        {isProductOwnerManage && isInventoryOwner && (
          <ButtonGroup
            orientation="horizontal"
            sx={{
              background: 'transparent',
              boxShadow: 'none',
            }}
            variant="contained"
          >
            <Button
              disabled={!isProductOwnerManage && !isInventoryOwner}
              onClick={() => navigate('./create/reconsignments')}
              sx={{
                background: 'primary.main',
                boxShadow: 'none',
              }}
              variant="contained"
            >
              New Reconsignments
            </Button>
            <ContainedMDropdownMenuButton>
              <MenuItem
                onClick={() =>
                  setDrawerComponentProps({
                    Component: prps => <OneToOneReconsignment {...prps} />,
                    types: DRAWER_TYPES.createReconsignment,
                  })
                }
                rolesAllowed={rolesAllowed}
                value={DrawerValue}
              >
                New 1 to 1 Reconsignment
              </MenuItem>
              <MenuItem
                disabled={!isProductOwnerManage && !isInventoryOwner}
                onClick={() => navigate('./create/bulk-setup')}
              >
                New Bulk 1 to 1 Reconsignment
              </MenuItem>
              <MenuItem
                onClick={() =>
                  setDrawerComponentProps({
                    Component: AutoOneToOneDrawer,
                    types: DRAWER_TYPES.createReconsignment,
                  })
                }
                rolesAllowed={rolesAllowed}
                value={DrawerValue}
              >
                New Auto Reconsignment
              </MenuItem>
            </ContainedMDropdownMenuButton>
          </ButtonGroup>
        )}
      </Box>
    </Box>
  );
};

Header.propTypes = {
  definedValues: PropTypes.shape({ endDate: PropTypes.string, startDate: PropTypes.string })
    .isRequired,
  fetchCSVReport: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired,
  urlPrefix: PropTypes.string.isRequired,
};

export default Header;
